import React, { useState, useMemo } from "react";
import { useTrapSkuStore } from "../../stores/trapSkuStore";
import TrapCategoryForm from "./TrapCategoryForm";
import TrapCategoryEditForm from "./TrapCategoryEditForm";
import TrapsBulkActions from "./TrapsBulkActions";
import {
  SearchBar,
  RefreshButton,
  FilterModal,
  FilterButton,
  PermissionWrapper,
  Dropdown,
  AnimatedCheckboxButton,
} from "components";
import { coinIcon } from "assets";
import { PLATFORM_OPTIONS } from "../../utils/trapSkuConfig";

// Компонент заголовку сторінки "Товари ловушок"
const TrapSkuHeader = () => {
  // Отримуємо дані та дії безпосередньо зі стору
  const isLoading = useTrapSkuStore((state) => state.isLoading);
  const fetchTrapSkus = useTrapSkuStore((state) => state.fetchTrapSkus);
  const filters = useTrapSkuStore((state) => state.filters);
  const applyFilters = useTrapSkuStore((state) => state.applyFilters);
  const setSearch = useTrapSkuStore((state) => state.setSearch);
  const shops = useTrapSkuStore((state) => state.shops);
  const trapCategories = useTrapSkuStore((state) => state.trapCategories);
  const trapCategoriesTree = useTrapSkuStore((state) => state.trapCategoriesTree);
  const crmCategories = useTrapSkuStore((state) => state.crmCategories);
  const createTrapCategory = useTrapSkuStore((state) => state.createTrapCategory);
  const updateTrapCategory = useTrapSkuStore((state) => state.updateTrapCategory);
  const trapData = useTrapSkuStore((state) => state.trapSkus);

  // Локальний стан для керування видимістю модального вікна фільтрів
  const [isFilterOpen, setFilterOpen] = useState(false);

  // Локальні стани для керування модальними вікнами створення та редагування категорій
  const [isCreateOpen, setCreateOpen] = useState(false);
  const [isEditOpen, setEditOpen] = useState(false);

  // Формуємо масив SKU, які відображаються на поточній сторінці
  const currentSkusOnPage = useMemo(() => {
    return (trapData || []).map((item) => item.sku);
  }, [trapData]);

  // Опції дій для керування категоріями пасток
  const categoryDropdownOptions = [
    { value: "create", label: "Створити нову категорію" },
    { value: "edit", label: "Редагувати категорію" },
  ];

  // Обробник вибору дії зі спадного меню категорій
  const handleCategoryActionChange = (actionType) => {
    if (actionType === "create") setCreateOpen(true);
    if (actionType === "edit") setEditOpen(true);
  };

  return (
    <div className="tapsku-title">
      <div className="tapsku-title-line">
        <div className="title-container">
          <RefreshButton onRefresh={fetchTrapSkus} disabled={isLoading} />
          <h3>Ловушки</h3>

          <div className="search-container">
            <SearchBar
              value={filters.search}
              onSearch={(value) => setSearch(value)}
              placeholder="Пошук SKU, товару, магазину..."
            />
            <FilterButton
              onClick={() => setFilterOpen(true)}
              isActive={
                filters.shop != null ||
                filters.platform != null ||
                filters.category_id != null
              }
            />

            <FilterModal
              isOpen={isFilterOpen}
              onClose={() => setFilterOpen(false)}
              onApply={(newFilters) => applyFilters(newFilters)}
              filters={[
                {
                  id: "platform",
                  label: "Платформа",
                  options: PLATFORM_OPTIONS,
                  value: filters.platform,
                  placeholder: "Усі платформи",
                  type: "select",
                  valueKey: "value",
                  labelKey: "label",
                },
                {
                  id: "shop",
                  label: "Магазин",
                  options: shops,
                  value: filters.shop,
                  placeholder: "Усі магазини",
                  type: "autocomplete",
                  valueKey: "id",
                  labelKey: "name",
                },
                {
                  id: "category_id",
                  label: "Категорія пастки",
                  options: trapCategoriesTree,
                  value: filters.category_id,
                  placeholder: "Усі категорії пасток",
                  type: "autocomplete",
                  valueKey: "id",
                  labelKey: "name",
                  isHierarchical: true,
                },
              ]}
            />
          </div>
        </div>
        <PermissionWrapper requiredPermission="traps:edit">
          <div className="bulk-actions-wrapper">
            <TrapsBulkActions
              currentSkusOnPage={currentSkusOnPage}
              isEmpty={!trapData.length}
            />
          </div>
        </PermissionWrapper>
      </div>

      <div className="tapsku-title-line">
        <PermissionWrapper
          requiredPermission="traps:edit-categories"
          fallback={<div></div>}
        >
          <div className="pricetag-button-container">
            <Dropdown
              options={categoryDropdownOptions}
              selected={null}
              onChange={handleCategoryActionChange}
              placeholder="Керування категоріями"
              mode="select"
              disabled={isLoading}
            />
          </div>

          <TrapCategoryForm
            isOpen={isCreateOpen}
            onClose={() => setCreateOpen(false)}
            allCategories={trapCategories}
            crmCategories={crmCategories}
            onCreate={createTrapCategory}
            disabled={isLoading}
          />

          <TrapCategoryEditForm
            isOpen={isEditOpen}
            onClose={() => setEditOpen(false)}
            allCategories={trapCategories}
            crmCategories={crmCategories}
            onUpdate={updateTrapCategory}
            disabled={isLoading}
          />
        </PermissionWrapper>
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <AnimatedCheckboxButton
            id="trap-has-custom-price-toggle"
            label="Кастомні ціни"
            icon={coinIcon}
            checked={filters.only_custom}
            onChange={(e) => applyFilters({ only_custom: e.target.checked })}
          />
        </div>
      </div>
    </div>
  );
};

export default TrapSkuHeader;
